// ============================================================
// leituras.js — pedir ao banco só o que a tela mostra
//
// O painel responde por 99% das leituras do projeto, e a causa era uma só,
// repetida em seis telas: cada uma baixava o histórico INTEIRO de presenças
// do núcleo e filtrava o mês no navegador. Em setembro de 2026 isso era
// 2.499 documentos por abertura para mostrar 434, e a diferença crescia
// todo mês, porque o histórico cresce e o mês não.
//
// Aqui ficam as duas peças que resolvem isso, separadas do firebase.js para
// poderem ser testadas sem subir um projeto Firebase:
//
//   intervaloDoMes   o primeiro e o último dia, no formato que o banco
//                    guarda, para a consulta filtrar em vez do navegador.
//
//   criarCache       a mesma pergunta feita duas vezes em um minuto é
//                    respondida uma vez. Início, Relatório e Presença leem
//                    o mesmo mês, cada um por conta própria, e trocar de
//                    aba relia tudo.
//
// A validade é curta de propósito. Cache de dado de frequência que dura
// muito vira coordenador olhando presença de dez minutos atrás e achando
// que o atleta não registrou.
// ============================================================

const VALIDADE_PADRAO_MS = 60 * 1000;

// Primeiro e último dia do mês, em ISO.
//
// mes é 0-11, como no Date do JavaScript e como as telas já usam. O último
// dia sai do dia 0 do mês seguinte, que é o truque que acerta fevereiro e
// ano bissexto sem tabela.
export function intervaloDoMes(mes, ano) {
  const m = Number(mes);
  const a = Number(ano);
  if (!Number.isFinite(m) || !Number.isFinite(a)) return null;
  const doisDigitos = (n) => String(n).padStart(2, "0");
  const ultimo = new Date(a, m + 1, 0).getDate();
  return {
    de: `${a}-${doisDigitos(m + 1)}-01`,
    ate: `${a}-${doisDigitos(m + 1)}-${doisDigitos(ultimo)}`,
  };
}

// O intervalo que cobre uma lista de meses, do primeiro dia do mais antigo
// ao último dia do mais novo. Serve ao comparativo, que olha até 12 meses.
export function intervaloDosMeses(meses) {
  const lista = (Array.isArray(meses) ? meses : [])
    .map((x) => intervaloDoMes(x.mes, x.ano))
    .filter(Boolean);
  if (!lista.length) return null;
  return {
    de: lista.reduce((menor, x) => (x.de < menor ? x.de : menor), lista[0].de),
    ate: lista.reduce((maior, x) => (x.ate > maior ? x.ate : maior), lista[0].ate),
  };
}

export function criarCache({ validadeMs = VALIDADE_PADRAO_MS, agora = () => Date.now() } = {}) {
  const guardado = new Map();

  return {
    // Guarda a PROMESSA, e não o resultado: duas telas que abrem juntas
    // perguntam ao mesmo tempo, e guardar só o resultado deixaria as duas
    // consultas saírem antes de a primeira voltar.
    async obter(chave, buscar) {
      const antigo = guardado.get(chave);
      if (antigo && agora() - antigo.quando < validadeMs) return antigo.promessa;

      const promessa = Promise.resolve().then(buscar);
      guardado.set(chave, { quando: agora(), promessa });

      // Consulta que falhou não fica guardada: senão o erro de rede de um
      // segundo vira um minuto de tela vazia.
      promessa.catch(() => {
        const atual = guardado.get(chave);
        if (atual && atual.promessa === promessa) guardado.delete(chave);
      });

      return promessa;
    },

    // Chamado depois de gravar. Sem isto, quem registra presença e volta
    // para a tela vê a lista de antes e acha que o registro não entrou.
    invalidar(prefixo) {
      if (!prefixo) { guardado.clear(); return; }
      [...guardado.keys()]
        .filter((k) => k.startsWith(prefixo))
        .forEach((k) => guardado.delete(k));
    },

    get tamanho() { return guardado.size; },
  };
}
